import type { FileTypeDefinition } from "./experience";
import {
  normalizeComponentType,
  type FieldDefinition,
  type FileViewMode,
  type LayoutComponent,
  type VisibilityCondition,
} from "./templates";
import type { DisplayVisibility, JsonObject, JsonValue, WorldDocument } from "./types";
import { layoutComponentsFromDocument } from "./layouts";
import { projectComputedFields } from "./computed-fields";
import { playerVisibleDocuments } from "./presentation";
import { privateField, type StructuredSchema } from "./structured-schema";

export type FileViewAudience = "player" | "creator" | "gm";

export interface RenderedFileNode {
  id: string;
  type: LayoutComponent["type"];
  title?: string;
  binding?: string;
  value?: JsonValue;
  props?: LayoutComponent["props"];
  columns?: number;
  styleToken?: string;
  responsive?: LayoutComponent["responsive"];
  children: RenderedFileNode[];
}

export interface RenderedFileView {
  fileId: string;
  fileTypeId: string;
  mode: FileViewMode;
  title: string;
  summary: string;
  accent?: string;
  nodes: RenderedFileNode[];
}

const displayAllows = (visibility: DisplayVisibility, audience: FileViewAudience) => visibility === "public" || visibility === "player" || visibility === "creator" && audience === "creator" || visibility === "gm" && (audience === "gm" || audience === "creator") || visibility === "hidden" && audience === "creator";

export function canDisplayFile(document: WorldDocument, audience: FileViewAudience): boolean {
  return !document.deletedAt && displayAllows(document.displayVisibility ?? "player", audience);
}

function valueAt(document: WorldDocument, binding: string): JsonValue | undefined {
  if (binding === "title" || binding === "fields.title" || binding === "fields.name") {
    return document.title || (document.fields?.name as string) || (document.fields?.title as string);
  }
  if (binding === "summary" || binding === "fields.summary" || binding === "fields.description") {
    return document.summary || (document.fields?.summary as string) || (document.fields?.description as string);
  }
  if (binding === "kind" || binding === "fields.kind") return document.kind;
  if (binding === "image" || binding === "fields.image" || binding === "fields.cover") {
    return (document.fields?.image as JsonValue) || (document.fields?.cover as JsonValue) || (document.fields?.imageUrl as JsonValue) || (document as any).images?.[0] || "";
  }
  const path = binding.replace(/^fields\./, "").split(".").filter(Boolean);
  let current: unknown = document.fields;
  for (const segment of path) {
    if (!current || typeof current !== "object" || ["__proto__", "constructor", "prototype"].includes(segment)) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current as JsonValue | undefined;
}

function fieldForBinding(fields: readonly FieldDefinition[], binding?: string): FieldDefinition | undefined {
  if (!binding?.startsWith("fields.")) return undefined;
  const [root] = binding.slice(7).split(".");
  return fields.find((field) => field.id === root);
}

function fieldAllows(field: FieldDefinition | undefined, audience: FileViewAudience): boolean {
  if (!field?.visibleTo?.length) return true;
  return field.visibleTo.some((visibility) => displayAllows(visibility, audience));
}

function conditionMatches(document: WorldDocument, condition: VisibilityCondition): boolean {
  const value = valueAt(document, condition.field);
  if (condition.operator === "exists") return value != null && value !== "";
  if (condition.operator === "equals") return JSON.stringify(value) === JSON.stringify(condition.value);
  if (condition.operator === "not_equals") return JSON.stringify(value) !== JSON.stringify(condition.value);
  return typeof value === "number" && typeof condition.value === "number" && value > condition.value;
}

interface RepeatedItem { value: JsonValue; key: string; suffix: string }
function runtimeProps(document: WorldDocument, component: LayoutComponent, field?: FieldDefinition, audience: FileViewAudience = "creator", item?: RepeatedItem): JsonObject | undefined {
  const resolve = (value: JsonValue): JsonValue => {
    if (Array.isArray(value)) return value.map(resolve);
    if (value && typeof value === "object") {
      if (typeof value.$item === "string") {
        if (!item) return null;
        if (value.$item === "key" && (!item.value || typeof item.value !== "object" || !Object.hasOwn(item.value, "key"))) return item.key;
        let current: JsonValue | undefined = item.value;
        for (const key of value.$item.replace(/^\//, "").split(/[/.]/).filter(Boolean)) {
          if (["__proto__", "constructor", "prototype"].includes(key)) return null;
          current = current && typeof current === "object" ? (current as JsonObject)[key] : undefined;
        }
        return current ?? null;
      }
      if (typeof value.$state === "string") {
        const path = value.$state.replace(/^\//, "").replaceAll("/", ".");
        const binding = path === "name" || path === "title" ? "title" : path === "summary" || path === "description" ? "summary" : path === "image" ? "image" : `fields.${path}`;
        if (audience === "player" && privateField(binding.split(".")[1] || "")) return null;
        return valueAt(document, binding) ?? null;
      }
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolve(item)]));
    }
    return value;
  };
  const props = resolve(component.props ?? {}) as JsonObject;
  if (field?.editableBy?.length && !field.editableBy.includes(audience === "player" ? "player" : audience)) props.readOnly = true;
  const maxBinding = typeof props.maxBinding === "string" ? props.maxBinding : "";
  if (maxBinding) {
    const maximum = valueAt(document, maxBinding);
    if (typeof maximum === "number") props.max = maximum;
  }
  const fieldOptions = field?.options ?? field?.item?.options;
  if ((component.type === "select" || component.type === "voice_select") && !Array.isArray(props.options) && fieldOptions?.length) props.options = [...fieldOptions];
  return Object.keys(props).length ? props : undefined;
}

function sectionToComponents(section: { root: string; elements: Record<string, import("./layouts").LayoutElement> }): LayoutComponent[] {
  const visiting = new Set<string>();
  const visit = (id: string): LayoutComponent | null => {
    const el = section.elements[id];
    if (!el || visiting.has(id)) return null;
    visiting.add(id);
    const children = (el.children ?? []).map(visit).filter((c): c is LayoutComponent => c != null);
    visiting.delete(id);
    return {
      id,
      type: el.type,
      ...(el.props ? { props: structuredClone(el.props) } : {}),
      ...(el.title ? { title: el.title } : {}),
      ...(el.binding ? { binding: el.binding } : {}),
      ...(el.columns != null ? { columns: el.columns } : {}),
      ...(children.length ? { children } : {}),
      ...(el.repeatFrom ? { repeatFrom: el.repeatFrom } : {}),
      ...(el.visibleWhen?.length ? { visibleWhen: structuredClone(el.visibleWhen) } : {}),
      ...(el.styleToken ? { styleToken: el.styleToken } : {}),
      ...(el.responsive ? { responsive: structuredClone(el.responsive) } : {}),
    };
  };
  const root = visit(section.root);
  if (!root) return [];
  return root.id === "layout-root" && root.type === "stack" ? root.children ?? [] : [root];
}

function renderComponent(document: WorldDocument, fileType: FileTypeDefinition, component: LayoutComponent, audience: FileViewAudience, item?: RepeatedItem, referenceDocuments: readonly WorldDocument[] = []): RenderedFileNode | null {
  if (component.props?.importHidden === true) return null;
  if (audience === "player" && (privateField(component.binding?.split(".")[1] || "") || /gm[-_ ]|secret|hidden/i.test(component.id))) return null;
  if (!component.props?.importVisible && component.visibleWhen?.some((condition) => !conditionMatches(document, condition))) return null;
  if (!fieldAllows(fieldForBinding(fileType.fields, component.binding), audience)) return null;
  const boundValue = component.binding ? valueAt(document, component.binding) : undefined;
  const field = fieldForBinding(fileType.fields, component.binding);
  const normalizedComponentTitle = component.title?.trim().toLocaleLowerCase().replaceAll("_", " ");
  const normalizedFieldId = field?.id.trim().toLocaleLowerCase().replaceAll("_", " ");
  let title = (fileType.sourceSchema as StructuredSchema | undefined)?.properties?.[field?.id || ""]?.expression && !component.title ? undefined : field && (!component.title || normalizedComponentTitle === normalizedFieldId) ? field.label : component.title;
  // A hero is still useful before cover art exists: the native renderer paints
  // its gradient placeholder while keeping the file identity visible.
  const props = runtimeProps(document, component, field, audience, item);
  const visibility = component.props?.importVisible;
  if (visibility != null) {
    const resolved = props?.importVisible;
    const condition = visibility && typeof visibility === "object" && !Array.isArray(visibility) ? visibility : {};
    const visible = "eq" in condition ? JSON.stringify(resolved) === JSON.stringify(condition.eq) : "gt" in condition ? typeof resolved === "number" && resolved > Number(condition.gt) : Array.isArray(resolved) ? resolved.length > 0 : Boolean(resolved);
    if (!visible) return null;
  }
  if (typeof props?.label === "string" && props.label) title = props.label;

  // Resolve include and section_switch
  const layoutDoc = fileType.layouts?.full ?? fileType.layout;
  const sections = layoutDoc && typeof layoutDoc === "object" && "sections" in layoutDoc && layoutDoc.sections ? (layoutDoc.sections as Record<string, { name?: string; root: string; elements: Record<string, import("./layouts").LayoutElement> }>) : undefined;
  let sectionChildren: RenderedFileNode[] = [];
  if (component.type === "include" && typeof props?.section === "string" && sections?.[props.section]) {
    const comps = sectionToComponents(sections[props.section]!);
    sectionChildren = comps.map(comp => renderComponent(document, fileType, comp, audience, item, referenceDocuments)).filter((node): node is RenderedFileNode => node != null);
  }
  if (component.type === "section_switch" && props?.statePath && sections) {
    const stateVal = valueAt(document, `fields.${String(props.statePath).replace(/^\//, "").replaceAll("/", ".")}`);
    const cases = Array.isArray(props.cases) ? props.cases : [];
    const matched = cases.find(c => c && typeof c === "object" && (c as Record<string, unknown>).equals === stateVal) as { section: string } | undefined ?? (props.default ? { section: String(props.default) } : undefined);
    if (matched?.section && sections[matched.section]) {
      const comps = sectionToComponents(sections[matched.section]!);
      sectionChildren = comps.map(comp => renderComponent(document, fileType, comp, audience, item, referenceDocuments)).filter((node): node is RenderedFileNode => node != null);
    }
  }

  // Resolve reference_value live from referenced documents
  let resolvedRefValue: JsonValue | undefined;
  if (component.type === "reference_value" && referenceDocuments.length > 0) {
    const sourceObj = props?.source ?? (component.binding ? valueAt(document, component.binding) : undefined);
    const targetRefId = typeof sourceObj === "string" ? sourceObj : sourceObj && typeof sourceObj === "object" ? ((sourceObj as Record<string, unknown>).referenceId ?? (sourceObj as Record<string, unknown>).slug ?? (sourceObj as Record<string, unknown>).id) : undefined;
    const targetDoc = referenceDocuments.find((d) => d.id === targetRefId || d.fields.slug === targetRefId || d.title.toLowerCase().replaceAll(" ", "-") === targetRefId);
    if (targetDoc) {
      const fieldPath = typeof props?.field === "string" ? props.field : "";
      if (fieldPath === "$title" || fieldPath === "$name") {
        resolvedRefValue = targetDoc.title;
      } else if (fieldPath) {
        resolvedRefValue = valueAt(targetDoc, `fields.${fieldPath.replace(/^\//, "").replaceAll("/", ".")}`);
      }
    }
  }

  const repeated = component.repeatFrom ? valueAt(document, component.repeatFrom) : undefined;
  const repeatEntries: [string, JsonValue][] = Array.isArray(repeated) ? repeated.slice(0, 200).map((value, index) => [String(index), value]) : repeated && typeof repeated === "object" ? Object.entries(repeated).slice(0, 200) : [];
  const baseChildren = component.repeatFrom ? repeatEntries.flatMap(([key, value]) => {
    const keyField = typeof props?.importRepeatKey === "string" ? props.importRepeatKey : "key";
    const stableKey = value && typeof value === "object" && !Array.isArray(value) && typeof value[keyField] === "string" ? value[keyField] as string : key;
    return (component.children ?? []).map(child => renderComponent(document, fileType, child, audience, { value, key: stableKey, suffix: `${item?.suffix ?? ""}:${component.id}:${key}` }, referenceDocuments));
  }).filter((node): node is RenderedFileNode => node != null) : (component.children ?? []).map(child => renderComponent(document, fileType, child, audience, item, referenceDocuments)).filter((node): node is RenderedFileNode => node != null);
  const children = sectionChildren.length > 0 ? [...baseChildren, ...sectionChildren] : baseChildren;
  if (component.repeatFrom && !children.length) return null;
  if (props && Array.isArray(props.tabs)) { const tabs = props.tabs; props.tabs = (component.children ?? []).flatMap((child, index) => children.some(node => node.id === child.id) && tabs[index] != null ? [tabs[index]!] : []); }
  const value = resolvedRefValue ?? boundValue ?? props?.items ?? props?.content ?? props?.value ?? props?.text ?? props?.image ?? props?.src;
  if (component.binding && value === undefined && !children.length && component.type !== "image_header") return null;
  const edit = props?.editPath;
  const editPath = edit && typeof edit === "object" && !Array.isArray(edit) && typeof edit.value === "string" ? edit.value.replaceAll("{key}", item?.key ?? "") : "";
  const binding = editPath ? `fields.${editPath.replace(/^\//, "").replaceAll("/", ".")}` : component.binding;
  if (editPath && props) {
    const editableField = fieldForBinding(fileType.fields, binding);
    props.readOnly = Boolean(editableField?.editableBy?.length && !editableField.editableBy.includes(audience === "player" ? "player" : audience));
  }
  if (binding && audience === "player" && privateField(binding.split(".")[1] || "")) return null;
  return { id: component.id + (item?.suffix ?? ""), type: normalizeComponentType(component.type), ...(title ? { title } : {}), ...(binding ? { binding } : {}), ...(value === undefined ? {} : { value }), ...(props ? { props } : {}), ...(component.columns != null ? { columns: component.columns } : {}), ...(component.styleToken ? { styleToken: component.styleToken } : {}), ...(component.responsive ? { responsive: structuredClone(component.responsive) } : {}), children };
}

function fallbackComponentForField(field: FieldDefinition): LayoutComponent {
  const binding = `fields.${field.id}`;
  const type: LayoutComponent["type"] = field.type === "long_text" ? "markdown"
    : field.type === "image" ? "image"
    : field.type === "number" ? "stat_grid"
    : field.type === "boolean" ? "toggle"
    : field.type === "reference" ? "reference_grid"
    : field.type === "list" ? "badge_list"
    : field.type === "object" ? "field_list"
    : "text";
  return { id: `field-${field.id}`, type, title: field.label, binding };
}

/** Honest field-by-field display used until a creator applies a custom layout. */
export function schemaFallbackLayout(fileType: FileTypeDefinition, mode: FileViewMode): LayoutComponent[] {
  if (mode === "mention") return [{ id: "mention-title", type: "text", binding: "title", styleToken: "accent" }];
  const visibleFields = fileType.fields.filter((field) => field.contextPriority !== "never");
  const selected = mode === "compact" ? visibleFields.slice(0, 6) : visibleFields;
  const children = selected.map(fallbackComponentForField);
  return [{ id: `${mode}-schema-fallback`, type: mode === "compact" ? "grid" : "stack", ...(mode === "compact" ? { columns: 2 } : {}), children }];
}

/**
 * Converts one File source of truth into any registered human view. No card,
 * mention or sidebar copy is created, so every renderer observes one value.
 */
export function renderFileView(
  document: WorldDocument,
  fileType: FileTypeDefinition,
  mode: FileViewMode,
  audience: FileViewAudience,
  referenceDocuments: readonly WorldDocument[] = []
): RenderedFileView | null {
  if (!canDisplayFile(document, audience)) return null;
  const configuredLayout = layoutComponentsFromDocument(fileType.layouts?.[mode] ?? fileType.layout);
  const layout = configuredLayout.length ? configuredLayout : schemaFallbackLayout(fileType, mode);
  const nodes = layout.map((component) => renderComponent(document, fileType, component, audience, undefined, referenceDocuments)).filter((node): node is RenderedFileNode => node != null);
  return {
    fileId: document.id,
    fileTypeId: fileType.id,
    mode,
    title: document.title,
    summary: document.summary,
    ...(typeof document.fields.accent === "string" ? { accent: document.fields.accent } : {}),
    nodes,
  };
}

export interface OverviewSectionView {
  fileTypeId: string;
  fileTypeName: string;
  designation: string;
  category?: string;
  displayOrder: number;
  totalFileCount: number;
  files: RenderedFileView[];
}

export interface StoryOverviewView {
  storyId: string;
  sections: OverviewSectionView[];
}

/**
 * Compiles structured Overview Hub for a story/container matching Craft RPG's /overview endpoint.
 * Groups documents by File Types that have overviewDisplayOrder configured, rendering featured files in compact mode.
 */
export function compileStoryOverviewView(
  storyId: string,
  documents: readonly WorldDocument[],
  fileTypes: readonly FileTypeDefinition[],
  audience: FileViewAudience = "player"
): StoryOverviewView {
  const visibleDocs = documents.filter((doc) => !doc.deletedAt && canDisplayFile(doc, audience));
  const overviewFileTypes = fileTypes
    .filter((ft) => ft.overviewDisplayOrder != null && Number.isFinite(ft.overviewDisplayOrder))
    .sort((a, b) => (a.overviewDisplayOrder ?? 0) - (b.overviewDisplayOrder ?? 0));

  const sections: OverviewSectionView[] = [];

  for (const ft of overviewFileTypes) {
    const docsForType = visibleDocs.filter((doc) => doc.fileTypeId === ft.id || doc.kind === ft.designation);
    if (!docsForType.length) continue;

    const featuredSet = new Set(ft.featuredFileIds ?? []);
    // Featured files first, then remaining sorted by title
    const orderedDocs = [
      ...docsForType.filter((d) => featuredSet.has(d.id)),
      ...docsForType.filter((d) => !featuredSet.has(d.id)).sort((a, b) => a.title.localeCompare(b.title)),
    ];

    const renderedFiles = orderedDocs
      .slice(0, 12)
      .map((doc) => renderFileView(doc, ft, "compact", audience))
      .filter((v): v is RenderedFileView => v != null);

    sections.push({
      fileTypeId: ft.id,
      fileTypeName: ft.name,
      designation: ft.designation,
      category: (ft as any).category ?? undefined,
      displayOrder: ft.overviewDisplayOrder ?? 0,
      totalFileCount: docsForType.length,
      files: renderedFiles,
    });
  }

  return {
    storyId,
    sections,
  };
}
